import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { useWidget } from "/imports/state/WidgetContext";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import RecommendationSlotCard from "./RecommendationSlotCard";
import { CATEGORIES, cartItemFor, shortDateLabel } from "./recommendationsHelpers";

function RecommendationsSection({ reloadKey, onEditPreferences }) {
  const navigate = useNavigate();
  const { slug, currency, addToCart } = useWidget();

  // null = "All" (mixed, soonest across days); otherwise an ISO date filter.
  const [selectedDate, setSelectedDate] = useState(null);
  // Reset the date filter whenever the feed reloads so it never points at a
  // date that dropped out of the refreshed result.
  useEffect(() => setSelectedDate(null), [reloadKey]);

  // Book exactly like a slot-click in the booking flow: load the cart for the
  // chosen option and go straight to checkout. Rendered for signed-in users
  // only, so no login redirect.
  const bookSlot = (slot, option, courtIsOurs = true) => {
    const [type, item] = cartItemFor(slot, option, currency, courtIsOurs);
    addToCart(type, item);
    navigate(`/${slug}/checkout`);
  };

  const { data, loading, error } = useFetch(() => api.getUserRecommendations(slug), [slug, reloadKey], { skip: !slug });

  // Silently no-op on error — recommendations are a non-critical enhancement and
  // shouldn't push an error banner onto the page.
  if (error) return null;

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your next session
          </Typography>
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const meta = data?.meta || {};
  // explicit / mixed / implicit / none (implicit = derived from booking history).
  const personalization = meta.personalization || "none";
  const personalized = personalization !== "none";
  const horizon = meta.horizonDays || 14;
  // Every date present in the feed gets a chip — the strip scrolls, and a
  // capped strip would hide days the "All" view still shows.
  const dates = Array.isArray(meta.dates) ? meta.dates : [];
  const hasAny = CATEGORIES.some((c) => (data?.[c.key] || []).length > 0);
  const title = "Your next session"; // non-personalized clients get the set-preferences prompt instead of a feed
  const subtitle =
    personalization === "implicit"
      ? "Based on your booking history"
      : personalization === "mixed"
        ? "Based on your preferences and booking history"
        : null;
  const prefsCta = meta.status === "pending" ? "Set preferences" : "Edit preferences";

  const bookAgain = meta.bookAgain || null;
  const dayLabel = (key) => (key ? key.charAt(0).toUpperCase() + key.slice(1) : "");
  // Mobile stacks a plain header over a full-width card to avoid a card-in-a-card;
  // from sm up it's the green hero with the text and card side by side.
  const bookAgainHero = bookAgain && (
    <Box
      sx={{
        mb: 2,
        p: { xs: 0, sm: 1.5 },
        borderRadius: 2,
        border: { xs: "none", sm: "1px solid" },
        borderColor: { sm: "primary.main" },
        bgcolor: { xs: "transparent", sm: "rgba(72, 201, 176, 0.06)" },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1.5,
        flexWrap: { sm: "wrap" }
      }}
    >
      <Box sx={{ flex: { sm: "1 1 10rem" }, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Book again
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
          Your usual: {bookAgain.usual?.serviceName}
          {bookAgain.usual?.trainerName ? ` with ${bookAgain.usual.trainerName}` : ""}
          {bookAgain.usual?.weekdayKey ? ` · ${dayLabel(bookAgain.usual.weekdayKey)}s ${bookAgain.usual.timeLabel || ""}` : ""}
        </Typography>
      </Box>
      <RecommendationSlotCard slot={bookAgain.slot} currency={currency} onBook={bookSlot} fullWidth />
    </Box>
  );

  // Slots to render for a category: the picked day, or — for "All" — the full
  // soonest-first list as returned. The backend already caps per day and overall,
  // so "All" stays a bounded, chronological feed; the date strip is how the
  // client narrows to a single day.
  const viewSlots = (slots) => (selectedDate ? slots.filter((s) => s.date === selectedDate) : slots);

  // No preferences and no booking history — the feed surfaces no slots at all
  // (the backend returns none): just the book-again hero when one exists and a
  // prompt to set preferences.
  if (!personalized) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your next session
          </Typography>
          {bookAgainHero}
          <Typography variant="body2" color="textSecondary">
            Set your preferences and we&apos;ll suggest sessions that fit how you play.
          </Typography>
          {onEditPreferences && (
            <Button size="small" onClick={onEditPreferences} sx={{ mt: 1 }}>
              Set preferences
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Nothing across any category — keep the page clean with a single soft
  // prompt rather than three empty rows.
  if (!hasAny) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {bookAgainHero}
          <Typography variant="body2" color="textSecondary">
            {`Nothing matched your preferences in the next ${horizon} days.${meta.budgetExhausted ? " Try a single preferred location to widen the search." : " Try widening your preferred times or interests."}`}
          </Typography>
          {onEditPreferences && (
            <Button size="small" onClick={onEditPreferences} sx={{ mt: 1 }}>
              Update preferences
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
        {/* One row even on a narrow phone: wrapping dropped the button onto its own
            line between the title and the subtitle, where it read as a stray link. */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={subtitle ? 0.5 : 2} gap={1}>
          {/* Smaller only on the narrowest phones (~360px), where both don't fit at full size. */}
          <Typography variant="h6" sx={{ minWidth: 0, "@media (max-width: 380px)": { fontSize: "1.1rem" } }}>
            {title}
          </Typography>
          {onEditPreferences && (
            <Button size="small" onClick={onEditPreferences} sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
              {prefsCta}
            </Button>
          )}
        </Box>
        {subtitle && (
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1.5 }}>
            {subtitle}
          </Typography>
        )}

        {bookAgainHero}

        {/* Picking a home location collapses the main search cost and sharpens results. */}
        {meta.locationPreferenceEmpty && onEditPreferences && (
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1.5 }}>
            Tip: pick your preferred club in preferences for faster, more relevant suggestions.
          </Typography>
        )}

        {/* Optional date strip — only when matches span more than one day. */}
        {dates.length > 1 && (
          <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1.5, mb: 1, scrollbarWidth: "thin" }}>
            <Chip
              label="All"
              size="small"
              color={selectedDate === null ? "primary" : "default"}
              variant={selectedDate === null ? "filled" : "outlined"}
              onClick={() => setSelectedDate(null)}
              sx={{ flex: "0 0 auto" }}
            />
            {dates.map((d) => (
              <Chip
                key={d}
                label={shortDateLabel(d)}
                size="small"
                color={selectedDate === d ? "primary" : "default"}
                variant={selectedDate === d ? "filled" : "outlined"}
                onClick={() => setSelectedDate(d)}
                sx={{ flex: "0 0 auto" }}
              />
            ))}
          </Box>
        )}

        {CATEGORIES.map((cat, idx) => {
          const slots = viewSlots(data?.[cat.key] || []);
          return (
            <Box
              key={cat.key}
              sx={{
                mt: idx === 0 ? 0 : 2.5,
                pt: idx === 0 ? 0 : 2.5,
                borderTop: idx === 0 ? "none" : "1px solid",
                borderColor: "divider"
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                {cat.label}
              </Typography>
              {slots.length > 0 ? (
                <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1, scrollbarWidth: "thin" }}>
                  {slots.map((slot, i) => (
                    <RecommendationSlotCard
                      key={`${slot.serviceId}-${slot.date}-${slot.timeId}-${slot.locationId}-${slot.bookishId || i}`}
                      slot={slot}
                      currency={currency}
                      onBook={bookSlot}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {selectedDate
                    ? `No ${cat.label.toLowerCase()} on ${shortDateLabel(selectedDate)}.`
                    : `No ${cat.label.toLowerCase()} matching your preferences in the next ${horizon} days.`}
                </Typography>
              )}
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default RecommendationsSection;
